import { Controller } from "@hotwired/stimulus"
import { get } from "@rails/request.js"
import { escapeHtml } from "../lib/html_sanitizer"
import { valueOrEmptyHtml } from "../lib/empty_value"
import { statusBadgeClass } from "../lib/status_badge"

export default class extends Controller {
  static targets = ["form", "codeInput", "submitBtn", "submitIdleLabel", "submitLoadingLabel", "result"]
  static values = { url: String }

  connect() {
    this._resetHandler = () => this.#reset()
    this.element.addEventListener("hidden.bs.modal", this._resetHandler)
  }

  disconnect() {
    this.element.removeEventListener("hidden.bs.modal", this._resetHandler)
  }

  async lookup(event) {
    event.preventDefault()
    const code = this.codeInputTarget.value.trim()
    if (!code) return

    this.#setLoading(true)

    try {
      const response = await get(this.urlValue, {
        query: { code },
        responseKind: "json"
      })

      if (response.ok) {
        const data = await response.json
        this.#renderFound(data)
      } else if (response.statusCode === 404) {
        this.#renderNotFound()
      } else {
        this.#renderError()
      }
    } catch {
      this.#renderError()
    } finally {
      this.#setLoading(false)
    }
  }

  #setLoading(isLoading) {
    this.submitBtnTarget.disabled = isLoading
    this.codeInputTarget.disabled = isLoading
    this.submitIdleLabelTarget.classList.toggle("d-none", isLoading)
    this.submitLoadingLabelTarget.classList.toggle("d-none", !isLoading)
  }

  #renderFound(data) {
    const statusBadge = data.active
      ? `<span class="${statusBadgeClass("active")}">Active</span>`
      : `<span class="${statusBadgeClass("inactive")}">Inactive</span>`

    const expires = data.ends_at
      ? `<div class="text-muted small mt-1">Expires ${escapeHtml(data.ends_at)}</div>`
      : ""

    this.resultTarget.innerHTML = `
      <div class="gl-card p-3">
        <div class="d-flex justify-content-between align-items-start mb-2">
          <div>
            <div class="text-muted small">Voucher Code</div>
            <div class="fw-bold">${escapeHtml(data.code)}</div>
          </div>
          ${statusBadge}
        </div>
        <div class="text-muted small">Name</div>
        <div class="mb-2">${valueOrEmptyHtml(data.name)}</div>
        <div class="text-muted small">Remaining Balance</div>
        <div class="fs-4 fw-bold text-success">${escapeHtml(data.balance)}</div>
        ${expires}
      </div>
    `
    this.resultTarget.classList.remove("d-none")
  }

  #renderNotFound() {
    this.resultTarget.innerHTML = `
      <div class="gl-alert gl-alert--warning mb-0" role="alert">
        <i class="ph-fill ph-warning-circle"></i>
        <div class="gl-alert-content">Value entered is not a Voucher Code.</div>
      </div>
    `
    this.resultTarget.classList.remove("d-none")
  }

  #renderError() {
    this.resultTarget.innerHTML = `
      <div class="gl-alert gl-alert--danger mb-0" role="alert">
        <i class="ph-fill ph-x-circle"></i>
        <div class="gl-alert-content">Something went wrong looking up that voucher. Please try again.</div>
      </div>
    `
    this.resultTarget.classList.remove("d-none")
  }

  #reset() {
    this.codeInputTarget.value = ""
    this.resultTarget.innerHTML = ""
    this.resultTarget.classList.add("d-none")
  }
}
